import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  FaExclamationCircle as ExclamationIcon,
} from 'react-icons/fa';
import Button from '../../app/buttons';
import RetryIcon from './retry-icon';
import { destroyMedium, uploadMediumFile } from '../actions';
import { selectMedium } from '../selectors';
import { singularize } from 'inflected';
import { Message, ButtonWrap, Title } from './styled';

// Shown when the file never arrived: either the sweep gave up on it
// (`stalled`) or this browser's attempt to PUT it failed.
//
// Two things used to be conflated here. `medium.state === 'stalled_upload' ||
// !medium.tmpUrl` chose the delete-only variant, so a medium we simply had no
// local copy of -- which is every medium after a page reload, since tmpUrl is a
// blob: url held in redux -- got the harshest wording and no retry. And this
// component was rendered for server-side PROCESSING failures too, telling people
// an upload had failed when the file was safely in storage. That now routes to
// Processing instead.
//
// The retry/delete split still keys on tmpUrl, because retrying genuinely needs
// the local file -- but the message no longer pretends that is the same thing as
// knowing what went wrong.
//
// COPY IS PLACEHOLDER -- see docs/upload_reliability_for_design.md.
const UploadPrompt = ({
  id, pageId, mediumType, storyId, medium, stalled, ...props
}) => (!medium.tmpUrl ?
  <Message>
    <Title>
      <ExclamationIcon size="24" />
      <span>
        {stalled ? "This Didn't Finish Uploading" : 'Upload Failed'}
      </span>
    </Title>
    <p>
      {stalled
        ? `This ${singularize(mediumType).toLowerCase()} didn't finish uploading, and we no longer have a copy in this browser to retry with. Add it again to try once more.`
        : `This ${singularize(mediumType).toLowerCase()} didn't upload, and we no longer have a copy in this browser to retry with. Add it again to try once more.`}
    </p>
    <ButtonWrap>
      <Button
        destructive
        onClick={() => (props.destroyMedium(
          id,
          pageId,
          mediumType,
          storyId,
        ))}
      >
        Delete
      </Button>
    </ButtonWrap>
  </Message> :
  <Message>
    <Title>
      <ExclamationIcon size="24" />
      <span>
        File didn't upload properly
      </span>
    </Title>
    <p>
      This can happen when uploading too many files at once or with a weak or interrupted connection.
    </p>
    {/* <ButtonWrap> */}
    <Button
      primary
      onClick={async () => {
        const file = await fetch(medium.tmpUrl).then(res => res.blob());
        return props.uploadMediumFile(storyId, medium, file);
      }}
    >
      <RetryIcon />
      Try Again
    </Button>
    <Button
      destructive
      onClick={() => (props.destroyMedium(
        id,
        pageId,
        mediumType,
        storyId,
      ))}
    >
      Remove File
    </Button>
    {/* </ButtonWrap> */}
  </Message>
);

UploadPrompt.propTypes = {
  stalled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  mediumType: PropTypes.string.isRequired,
  pageId: PropTypes.string.isRequired,
  storyId: PropTypes.string.isRequired,
  destroyMedium: PropTypes.func.isRequired,
  uploadMediumFile: PropTypes.func.isRequired,
  medium: PropTypes.shape({
  }).isRequired,
};

UploadPrompt.defaultProps = {
  stalled: false,
};

const mapStateToProps = (state, { id, mediumType }) => ({
  medium: selectMedium(state, id, mediumType),
});

export default connect(
  mapStateToProps,
  { destroyMedium, uploadMediumFile },
)(UploadPrompt);
